import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ArrowRightLeft, Clock, CheckCircle2, XCircle, FileText, ExternalLink, Loader2, ThumbsUp, ThumbsDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDT = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return iso; }
};

const badge = (estado) => {
  if (estado === 'pendiente') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-700"><Clock className="w-3 h-3" />Pendiente</span>;
  if (estado === 'aprobada') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" />Aprobada</span>;
  if (estado === 'rechazada') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Rechazada</span>;
  return <span className="text-xs text-slate-500">{estado}</span>;
};

export default function CapacitadorReasignaciones() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pendiente');
  const [rechazoModal, setRechazoModal] = useState(null); // {id, ...}
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/capacitador/reasignaciones`, { headers });
      setRows(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filtered = tab === 'todas' ? rows : rows.filter(r => r.estado === tab);
  const countByEstado = (e) => rows.filter(r => r.estado === e).length;

  const aprobar = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/capacitador/reasignaciones/${id}/aprobar`, {}, { headers });
      toast.success('Reasignación aprobada');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al aprobar'); }
    setBusy(false);
  };

  const rechazar = async () => {
    if (!motivoRechazo.trim()) { toast.error('Ingrese un motivo'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/capacitador/reasignaciones/${rechazoModal.id}/rechazar`, { motivo: motivoRechazo.trim() }, { headers });
      toast.success('Solicitud rechazada');
      setRechazoModal(null); setMotivoRechazo('');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al rechazar'); }
    setBusy(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cap-reasignaciones">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowRightLeft className="w-6 h-6 text-teal-600" />Solicitudes de Reasignación</h1>
        <p className="text-xs text-slate-500 mt-0.5">Aprueba o rechaza las solicitudes de reasignación de trabajadores entre empresas colaboradoras.</p>
      </div>

      <div className="inline-flex rounded-lg bg-slate-100 p-1 mb-4">
        {[
          { id: 'pendiente', lbl: 'Pendientes' },
          { id: 'aprobada', lbl: 'Aprobadas' },
          { id: 'rechazada', lbl: 'Rechazadas' },
          { id: 'todas', lbl: 'Todas' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            {t.lbl} {t.id !== 'todas' && <span className="ml-1 text-[10px] opacity-60">({countByEstado(t.id)})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ArrowRightLeft className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Sin solicitudes en {tab === 'todas' ? 'el sistema' : `estado "${tab}"`}.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-slate-500 font-medium">RUT</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Nombre</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Origen → Destino</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Solicitante</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Fecha</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Estado</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Carta</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Motivo</th>
                <th className="text-right px-4 py-3 text-slate-500 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`row-cap-reasig-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.rut}</td>
                  <td className="px-4 py-3">{r.nombre_snapshot}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{r.empresa_origen_nombre || <span className="text-slate-300">Sin empresa</span>}</div>
                    <div className="text-teal-700 font-semibold">→ {r.empresa_destino_nombre}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{r.solicitante_email}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDT(r.fecha_solicitud)}</td>
                  <td className="px-4 py-3">{badge(r.estado)}</td>
                  <td className="px-4 py-3">
                    {r.carta_url ? (
                      <a href={r.carta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline">
                        <FileText className="w-3 h-3" /> Ver <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[220px]">
                    {r.motivo || <span className="text-slate-300">—</span>}
                    {r.motivo_rechazo && <div className="text-red-600 mt-1">✕ {r.motivo_rechazo}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.estado === 'pendiente' ? (
                      <>
                        <button onClick={() => aprobar(r.id)} disabled={busy}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md mr-1.5 disabled:opacity-50"
                          data-testid={`aprobar-${r.id}`}>
                          <ThumbsUp className="w-3 h-3" />Aprobar
                        </button>
                        <button onClick={() => { setRechazoModal(r); setMotivoRechazo(''); }} disabled={busy}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-md disabled:opacity-50"
                          data-testid={`rechazar-${r.id}`}>
                          <ThumbsDown className="w-3 h-3" />Rechazar
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">{r.resuelto_por_email ? `Por ${r.resuelto_por_email}` : ''}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Motivo Rechazo */}
      {rechazoModal && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => !busy && setRechazoModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-3">Rechazar solicitud</h3>
            <p className="text-xs text-slate-500 mb-3">
              Trabajador <span className="font-semibold">{rechazoModal.nombre_snapshot}</span> hacia{' '}
              <span className="font-semibold">{rechazoModal.empresa_destino_nombre}</span>.
            </p>
            <label className="text-xs font-semibold text-slate-600">Motivo del rechazo *</label>
            <textarea value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)} rows={4}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
              placeholder="Ej. Carta no cumple con requisitos; falta firma del representante legal."
              data-testid="motivo-rechazo-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" disabled={busy} onClick={() => setRechazoModal(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">Cancelar</button>
              <button type="button" disabled={busy} onClick={rechazar}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="rechazar-confirm-btn">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Rechazando...</> : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
